import { chinaInapi } from "@/apis/chinaInapi";
import workOrderApi from "@/apis/workOrder";
import { jwtDecoder } from "@/utils/utils";

/**
 * 작업 지시서 관련 비즈니스 로직을 담당하는 서비스
 *
 * 작업지시서 저장 백엔드를 한국 inapi(@/axios) → china-inapi(chinaInapi)로 전환.
 * china-inapi POST /china/dental-lab/work-orders 로 라우팅되며,
 * dentalLaboratoryId 는 BE 가 JWT companyId 로 강제하므로 FE 값은 무시된다.
 */
export class WorkOrderService {
  constructor(store) {
    this.store = store;
    this.api = workOrderApi(chinaInapi, store);
  }

  /**
   * 작업 지시서 데이터 준비
   */
  prepareWorkOrderData(selectedClient, certificateInfo, workOrderData) {
    console.log("[prepareWorkOrderData] 입력 데이터 확인:", {
      selectedClient,
      certificateInfo,
      workOrderData,
    });

    const dentalLaboratoryId = jwtDecoder(
      localStorage.getItem("accessTokenManu")
    );

    // 기공소명 가져오기 - 여러 소스에서 시도
    let dentalLaboratoryName = "";

    // 1. Vuex store의 userInfo에서
    if (this.store.state.userInfo?.companyName) {
      dentalLaboratoryName = this.store.state.userInfo.companyName;
    }
    // 2. localStorage의 CompanyInfo에서
    else if (localStorage.getItem("CompanyInfo")) {
      try {
        const companyInfo = JSON.parse(localStorage.getItem("CompanyInfo"));
        dentalLaboratoryName = companyInfo?.name || "";
      } catch (error) {
        console.error("CompanyInfo 파싱 오류:", error);
      }
    }
    // 3. localStorage의 manufacturerNameManu에서
    else if (localStorage.getItem("manufacturerNameManu")) {
      dentalLaboratoryName = localStorage.getItem("manufacturerNameManu");
    }

    console.log("기공소 정보:", { dentalLaboratoryId, dentalLaboratoryName });
    console.log("차트번호:", certificateInfo.chartNumber);
    console.log("보철 납품일:", certificateInfo.worker?.prostheticDeliveryDate);

    if (!selectedClient || !certificateInfo || !workOrderData) {
      console.log("필수 데이터 누락:", {
        selectedClient: !!selectedClient,
        certificateInfo: !!certificateInfo,
        workOrderData: !!workOrderData,
      });
      return null;
    }

    // 제조사 정보 추출 (첫 번째 항목의 제조사 사용)
    const firstDetail = workOrderData.toothDetails?.[0];
    const manufacturer = {
      manufacturerId: firstDetail?.manufacturerId || "",
      name: firstDetail?.manufacturer || "",
    };

    // SPEC-DENTALMAX-MULTI-TOOTH-ISSUE-001 Wave2 DEC-009/REQ-ADM-MULTI-007 — UI 카드(제품 N개)를
    // "치아셋을 공유하는 toothDetail 엔트리 N개"로 분해한다. toothNumbers/treatmentType/material/stm은
    // 엔트리 전체가 동일하고, product/gtinCode/lotNumber/manufacturingDate/randomNo만 각 제품 값으로 갈린다.
    // 제품 0개 카드(malformed/구데이터 방어)는 발행 대상에서 제외한다(확정 단계에서 이미 차단되지만 방어적으로 skip).
    // SPEC-CHINA-CERT-CARD-LOOKUP-UNIFY-001 REQ-FE-STEP3-006 — randomNo를 함께 전송하여
    // BE(china-inapi dental-lab-work-orders createWorkOrder)가 원자 조건부 카드 배정에 사용하게 한다.
    const toothDetails = (workOrderData.toothDetails || []).flatMap((detail) => {
      const products = Array.isArray(detail.products) ? detail.products : [];
      if (products.length === 0) return [];
      return products.map((product) => ({
        toothNumbers: detail.toothNumbers || [],
        treatmentType: detail.treatmentType || "",
        product: product.itemName || "",
        material: detail.detailSelection || "ZIRCONIA",
        stm: detail.stm || false,
        gtinCode: product.gtinCode || "",
        lotNumber: product.lotNumber || "",
        manufacturingDate: product.manufacturingDate || "",
        randomNo: product.randomNo || "",
      }));
    });

    return {
      chartNumber: certificateInfo.chartNumber || "",
      prostheticDeliveryDate:
        certificateInfo.worker?.prostheticDeliveryDate || "",
      hospital: {
        hospitalId: selectedClient._id,
        name: selectedClient.name,
        cNo: selectedClient.cNo,
        ceo: selectedClient.ceo || "",
        call: selectedClient.call || "",
      },
      patient: {
        name: certificateInfo.name,
        mobile: certificateInfo.mobile?.replace(/\D/g, "") || "",
        displayMobile: certificateInfo.mobile || "",
        email: certificateInfo.email || "",
        countryCode: certificateInfo.countryCode || "+82",
        gender: certificateInfo.gender || "",
        birthDate: certificateInfo.birthDate || "",
        surgeryDate: certificateInfo.surgeryDate || "",
      },
      worker: {
        name: certificateInfo.worker?.name || "",
        workDate: certificateInfo.worker?.workDate || "",
      },
      // 시술 의사(옵셔널 자유입력). BE CreateWorkOrderDto.doctor({ name })와 1:1 —
      // 헝지우 경로와 같은 모양이라 실물카드 인쇄가 두 출처를 동일하게 읽는다.
      doctor: {
        name: certificateInfo.doctorName || "",
      },
      prosthetic: {
        prostheticType: "",
        selectedTeeth: workOrderData.selectedTeeth || [],
        manufacturer: manufacturer,
        toothDetails: toothDetails,
      },
      // agreements는 전송하지 않는다 — BE DTO에 없어 무음 폐기되던 필드로,
      // 동의 데이터가 작업지시서에 저장될 필요 없음이 확정되어 제거 (2026-07-16 감사 P1-1 결정)
      // 기본값을 여기서 다시 정하지 않는다 — Step2가 소유한 값을 그대로 싣는다.
      // 기본값이 두 곳에 흩어져 있으면 그 자체가 암묵지가 된다.
      metadata: {
        channel: certificateInfo.sendChannel,
        locale: certificateInfo.language,
      },
      dentalLaboratoryId,
      dentalLaboratoryName,
    };
  }

  /**
   * 작업 지시서 생성 API 호출
   */
  async createWorkOrder(workOrderData, onSuccess, onError) {
    try {
      console.log("=== API 호출 시작 ===");
      console.log("최종 요청 데이터:", JSON.stringify(workOrderData, null, 2));

      const response = await this.api.createWorkOrder(
        workOrderData,
        onSuccess,
        onError
      );
      return response;
    } catch (error) {
      console.error("작업 지시서 생성 실패:", error);
      if (onError) onError(error);
      throw error;
    }
  }
}
